import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { AppService } from '../../../app.service';
import {
  AdminGridCallbackEvent,
  AdminGridConfig,
  AdminGridDirective,
} from '../../shared/admin-grid.directive';
import { AdminDashboardEditComponent } from './admin-dashboard-edit/admin-dashboard-edit.component';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';

/**
 * Admin Dashboards list — migrated from
 * `GTW12/ui/app/components/admin/adminDashboards/adminDashboardCtrl.js`
 * (`adminDashboardListCtrl`) per ADMIN_TAB_MIGRATION_GUIDE.md.
 *
 * Reference implementation: AdminActionsComponent.
 */
@Component({
    selector: 'gtw-admin-dashboards',
    templateUrl: './admin-dashboards.component.html',
    styleUrls: ['./admin-dashboards.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminDashboardsComponent implements OnInit, OnDestroy {
  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  gridConfig!: AdminGridConfig;
  loading = false;

  private readonly ACTION_ID = 30156;
  private readonly GRID_ID = '5121';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private dialog: MatDialog,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    const clientKey =
      this.appService.USER_SETTINGS?.user?.client_key ??
      this.appService.USER_SETTINGS?.user?.client?.client_key ??
      '';
    this.gridConfig = {
      actionId: this.ACTION_ID,
      gridId: this.GRID_ID,
      extraParams: { client_key: clientKey },
      autoLoad: true,
      cacheKey: 'admin/admin-dashboards',
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridCallback(event: AdminGridCallbackEvent): void {
    const rowData = event.data?.rowData ?? event.data;
    this.openEditModal(rowData);
  }

  onGridDataLoaded(_data: any): void {
    this.loading = false;
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh();
  }

  addDashboard(): void {
    this.openEditModal(null);
  }

  private openEditModal(rowData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '90vw';
    config.maxWidth = '1100px';
    config.disableClose = true;
    config.data = {
      rowData: rowData || {},
      mode: rowData?.DASHBOARD_KEY || rowData?.DASHBOARD_ID ? 'edit' : 'new',
    };

    this.adminScreenContext.setRowData(rowData);
    const ref = this.dialog.open(AdminDashboardEditComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.adminScreenContext.setRowData({});
        if (result === 'saved') {
          this.adminGrid?.clearCache();
          this.adminGrid?.refresh();
        }
      });
  }
}
